import React from 'react';

import { useForm } from 'react-hook-form';
import { AccountProfileFeaturesType } from 'src/accounts/types';
import { Toast } from 'src/common/components';

import { updateAccount } from '../api';
import { Action } from '../constants';
import { useAccountProfileContext } from '../context';

const Features: React.FC = () => {
  const { state, dispatch } = useAccountProfileContext();
  const { account } = state;
  const { features } = account;
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, handleSubmit, reset } = useForm<AccountProfileFeaturesType>({
    defaultValues: features,
  });

  const onSubmit = async (data: AccountProfileFeaturesType): Promise<void> => {
    try {
      const response = await updateAccount(account.id, { ...data });
      dispatch({
        type: Action.UPDATE_ACCOUNT_FEATURES,
        features: response.data,
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
      reset(features);
    }
  };

  return (
    <form className="flex flex-col mt-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Bilingual enabled</h4>
          <p className="helper-text">
            Set to True for accounts that should use the language detection service to determine
            what language to use for the response.
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('sms_language_detection_enabled')}
          data-testid="sms-language-detection-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Calendar sync enabled</h4>
          <p className="helper-text">
            Controls if we should sync this account&#39;s data from their calendar.This setting only
            takes action if the customer has the BrainHi agent running on their server.
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('calendar_sync_enabled')}
          data-testid="calendar-sync-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Cancelations enabled</h4>
          <p className="helper-text">
            If on, BrainHi will cancel a patient&#39;s appointment if we detect that intent
            otherwise we will not allow patient&#39;s to cancel without contacting the office
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('cancelations_enabled')}
          data-testid="cancelations-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Confirmations enabled</h4>
          <p className="helper-text">
            Controls whether we attempt to send confirmations or not for this account&#39;s
            appointments
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('confirmations_enabled')}
          data-testid="confirmations-enabled"
        />
      </div>
      {features.confirmations_enabled && (
        <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div className="sm:col-span-2">
            <h4>Confirmations should include time</h4>
            <p className="helper-text">
              Controls whether the confirmation message sent by us includes the appointments date
              and time or just the date
            </p>
          </div>
          <input
            type="checkbox"
            className="w-5 h-5"
            {...register('confirmations_should_include_time')}
            data-testid="confirmations-should-include-time"
          />
        </div>
      )}
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Out of sync scheduling enabled</h4>
          <p className="helper-text">
            Set to True to for accounts that should allow appointment requests even if the account
            has gone out of sync.
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('out_of_sync_scheduling_enabled')}
          data-testid="out-of-sync-scheduling-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Scheduling enabled</h4>
          <p className="helper-text">
            If on, BrainHi will send a patient the scheduling link if we detect their intent to make
            an appointment. Otherwise no link is sent in the message.
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('scheduling_enabled')}
          data-testid="scheduling-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Telemedicine enabled</h4>
          <div className="flex">
            <p className="helper-text">Set to True for account that should active telemedicine.</p>
            <a
              className="ml-1 helper-text text-blue-1.0"
              target="_blank"
              rel="noreferrer"
              href="https://www.notion.so/brainhi/Configuraciones-para-mensajes-recordatorios-de-citas-por-telemedicina-a00885c310214a69b70a1eaec812d1c4"
            >
              (More info)
            </a>
          </div>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('has_telemedicine')}
          data-testid="has-telemedicine"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Text back enabled</h4>
          <p className="helper-text">
            If on, BrainHi will automatically text when a call is missed. if off, no text will be
            sent.
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('text_back_enabled')}
          data-testid="text-back-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
        <div className="sm:col-span-2">
          <h4>Voicemail enabled</h4>
          <p className="helper-text">
            If on, BrainHi will record and save patient voicemail when a new call is rescued.
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('voicemail_enabled')}
          data-testid="voicemail-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 ">
        <div className="sm:col-span-2">
          <h4>Waiting list enabled</h4>
          <p className="helper-text">Flag to control the release of the waiting list feature</p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('is_waiting_list_enabled')}
          data-testid="is-waiting-list-enabled"
        />
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 ">
        <div className="sm:col-span-2">
          <h4>New Missed Calls open new conversation sessions</h4>
          <p className="helper-text">
            Flag to control whether an incoming Missed Call always opens a new session in a
            conversation.
          </p>
        </div>
        <input
          type="checkbox"
          className="w-5 h-5"
          {...register('is_missed_call_open_thread')}
          data-testid="is-missed-call-open-thread"
        />
      </div>
      <button className="btn btn-secondary mt-4 ml-auto" type="submit" data-testid="submit">
        Save changes
      </button>
    </form>
  );
};

export default Features;
